import { AlertCircle, Loader2 } from "lucide-react";
import { melangerDeterministe, type PlacementsQsort } from "@repo/utils";
import { Button } from "./button";
import { Dialog } from "./dialog";
import type { QsortItem } from "./qsort-board-elements";

/**
 * Relecture de la pyramide avant l'envoi définitif. La validation fige les
 * réponses : sans ce palier, un clic de trop terminait l'épreuve sans que la
 * personne ait revu son classement d'un seul coup d'œil — ce que l'accordéon
 * mobile, qui n'ouvre qu'un niveau à la fois, ne permet jamais.
 *
 * L'erreur d'envoi s'affiche ICI, à côté du bouton qui l'a provoquée.
 */
export function QsortConfirmation({
  ouvert,
  onFermer,
  onConfirmer,
  enCours,
  erreur,
  items,
  pyramide,
  placements,
  libellesNiveaux,
  libelleConfirmer,
  graineOrdre,
}: {
  ouvert: boolean;
  onFermer: () => void;
  onConfirmer: () => void;
  enCours: boolean;
  erreur: string | null;
  items: QsortItem[];
  pyramide: number[];
  placements: PlacementsQsort;
  libellesNiveaux: string[];
  libelleConfirmer: string;
  /** Même graine que le tableau : la relecture reprend l'ordre qu'on a eu sous les yeux. */
  graineOrdre?: string;
}) {
  const ordonnes = graineOrdre ? melangerDeterministe(items, graineOrdre) : items;
  return (
    <Dialog
      isOpen={ouvert}
      onClose={enCours ? () => {} : onFermer}
      title="Relire avant d'envoyer"
      className="max-w-2xl"
    >
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Voici votre classement. Une fois envoyé, il ne pourra plus être
          modifié.
        </p>

        <dl className="divide-y divide-charte-line border-y border-charte-line">
          {pyramide.map((_, niveau) => {
            const libelles = ordonnes
              .filter((item) => placements[item.code] === niveau)
              .map((item) => item.label);
            return (
              <div
                key={niveau}
                className="grid gap-1 py-2.5 sm:grid-cols-[11rem_1fr] sm:gap-4"
              >
                <dt className="text-sm font-semibold">
                  {libellesNiveaux[niveau] ?? `Niveau ${niveau + 1}`}
                </dt>
                <dd className="text-sm text-muted-foreground">
                  {libelles.join(" · ")}
                </dd>
              </div>
            );
          })}
        </dl>

        {erreur && (
          <p
            role="alert"
            className="flex items-start gap-2 text-sm text-destructive"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              L'envoi n'a pas abouti : {erreur.replace(/\.?$/, ".")} Votre classement est conservé,
              vous pouvez réessayer.
            </span>
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onFermer} disabled={enCours}>
            Revenir au classement
          </Button>
          <Button onClick={onConfirmer} disabled={enCours}>
            {enCours && <Loader2 className="motion-safe:animate-spin" aria-hidden />}
            {libelleConfirmer}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
